import React, { useRef, useState } from "react";
import { Drawer, Divider, List, Skeleton } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";

import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { Button as UIButton } from "@/components/ui/button";
import useAxiosPrivateFile from "@/context/UseAxiosPrivateFileUploads";
import axios from "@/apiCalls/axios";
import { errorNotification, successNotification } from "@/utils/Notification";
import {
  exportFailedBeaconsDisableToExcel,
  exportFailedUploadBeaconsToExcel,
} from "@/utils/ExcelDataExport";

interface ProductsDrawerProps {
  showDisableBeaconDrawer: boolean;
  setshowDisableBeaconDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  getBeacons?: () => void; // optional refresh callback
}

const DisableBeaconsDrawer: React.FC<ProductsDrawerProps> = ({
  showDisableBeaconDrawer,
  setshowDisableBeaconDrawer,
  getBeacons,
}) => {
  const onClose = () => setshowDisableBeaconDrawer(false);

  // ======= Upload state/logic (unchanged) =======
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [failedata, setFailedData] = useState<any[]>([]);
  const axiosPrivateFile = useAxiosPrivateFile();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    if (
      selectedFile &&
      selectedFile.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setFile(selectedFile);
    } else {
      alert("Please upload a valid Excel file (.xlsx)");
    }
  };

  const handleCancelUpload = () => {
    setFile(null);
    setLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getClientIPAndURL = async () => {
    try {
      const response = await axios.get("https://api.ipify.org?format=json");
      const clientIp = response.data.ip;
      const currentUrl = window.location.href;
      return { clientIp, currentUrl };
    } catch (error) {
      console.error("Error fetching IP address:", error);
      return { clientIp: "Unknown", currentUrl: window.location.href };
    }
  };

  const onFinish = (formData: FormData) => {
    setLoading(true);
    let isMounted = true;
    const controller = new AbortController();

    getClientIPAndURL()
      .then(({ clientIp, currentUrl }) =>
        axiosPrivateFile.post("/api/v1/beacons/disableBeacons", formData, {
          signal: controller.signal,
          headers: {
            "X-Client-IP": clientIp,
            "X-Origin-URL": currentUrl,
          },
        })
      )
      .then((response: any) => response.data)
      .then((data) => {
        if (!isMounted) return;
        successNotification("File uploaded successfully!", "", "topRight");
        setFailedData(data); // backend may return failed rows
        if(data.length === 0){
       getBeacons?.();
        }
        // refresh if provided
        handleCancelUpload();
      })
      .catch((err: any) => {
        const errors = err?.response?.data?.errors;
        if (errors && errors?.[0]?.message === "token as expired") {
          const errMsg = errors?.[0]?.message;
          errorNotification("There was an issue", `${errMsg}`, "topRight");
        } else if (errors && Array.isArray(errors) && errors.length > 0) {
          errors.forEach((error: any) => {
            const errorMessage = error.message || "An unknown error occurred";
            errorNotification("There was an issue", `${errorMessage}`, "topRight");
          });
        } else {
          errorNotification("There was an issue", "No server response", "topRight");
        }
      })
      .finally(() => setLoading(false));

    return () => {
      isMounted = false;
      controller.abort();
    };
  };

  // ✅ same logic: just send the file to backend
  const handleUpload = async () => {
    setFailedData([]);
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    onFinish(formData);
  };

  const loadData = () => {
    // hook for pagination if your backend supports it
  };

  // ======= UI Helpers =======
  const truncatedName = (name: string, max = 36) =>
    name.length > max ? name.slice(0, max - 6) + "..." + name.slice(-6) : name;

  // ======= Drawer UI =======
  return (
    <Drawer
      title={
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Disable Beacons</h3>
            <p className="text-xs text-gray-500">
              Upload a single-column <span className="font-medium">.xlsx</span> with identifiers to disable.
            </p>
          </div>
          <UIButton
            variant="outline"
            onClick={() =>
              exportFailedBeaconsDisableToExcel(
                failedata,
                "Eezy-Track-beacons-disable-template"
              )
            }
            className="hidden sm:flex items-center gap-2"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Template
          </UIButton>
        </div>
      }
      width={720}
      onClose={onClose}
      open={showDisableBeaconDrawer}
      bodyStyle={{ paddingBottom: 96 }}
      style={{
        backgroundColor: "white",
        transition: "transform 0.3s ease, opacity 0.3s ease",
      }}
      maskStyle={{ transition: "opacity 0.3s ease" }}
      footer={
        <div className="flex items-center justify-end gap-2">
          <UIButton
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Close
          </UIButton>
          <UIButton
            onClick={handleUpload}
            disabled={!file || loading}
            className="disabled:opacity-50"
          >
            {loading ? "Uploading…" : "Upload & Disable"}
          </UIButton>
        </div>
      }
    >
      {/* Upload Zone */}
      <section className="space-y-4">
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
          }}
          className={[
            "w-full rounded-2xl border-2 border-dashed",
            loading ? "border-gray-200" : "border-gray-300 hover:border-gray-400",
            "bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer",
            "p-6"
          ].join(" ")}
        >
          <div className="flex flex-col items-center justify-center gap-2 text-center">
            <div className="text-sm text-gray-600">
              Drag & drop your <span className="font-medium">.xlsx</span> here
              <span className="text-gray-400"> — or — </span>
              <span className="text-blue-600">browse</span>
            </div>
            <div className="text-xs text-gray-400">
              Only Excel (.xlsx). Server will validate contents.
            </div>
          </div>
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            disabled={loading}
          />
        </div>

        {/* Selected file chip */}
        {file && (
          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2">
            <div className="truncate text-sm">
              <span className="font-medium text-gray-700">
                {truncatedName(file.name)}
              </span>
              <span className="ml-2 text-gray-400 text-xs">
                {(file.size / 1024).toFixed(0)} KB
              </span>
            </div>
            <div className="flex items-center gap-2">
              <UIButton
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
              >
                Change
              </UIButton>
              <button
                onClick={handleCancelUpload}
                disabled={loading}
                className="text-red-500 hover:text-red-600 text-sm font-medium disabled:opacity-50"
                aria-label="Remove selected file"
                title="Remove selected file"
              >
                Remove
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Actions for small screens */}
      <div className="mt-4 sm:hidden">
        <UIButton
          variant="outline"
          onClick={() =>
            exportFailedBeaconsDisableToExcel(
              failedata,
              "Eezy-Track-beacons-disable-template"
            )
          }
          className="w-full flex items-center justify-center gap-2"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Template
        </UIButton>
      </div>

      <Divider />

      {/* Failed rows section */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700">
            Failed rows
          </h4>
          {failedata.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                {failedata.length}
              </span>
              <UIButton
                variant="outline"
                className="flex items-center gap-2"
                onClick={() =>
                  exportFailedUploadBeaconsToExcel(
                    failedata,
                    "EezyTrackfailedUploadData"
                  )
                }
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Export
              </UIButton>
            </div>
          )}
        </div>

        {failedata.length === 0 ? (
          <p className="text-xs text-gray-400">
            No failed rows to display yet.
          </p>
        ) : (
          <InfiniteScroll
            dataLength={failedata?.length ?? 0}
            next={loadData}
            hasMore={false}
            loader={<Skeleton avatar paragraph={{ rows: 1 }} active />}
            endMessage={
              <Divider plain>{`🤐 Total failed beacon(s) ${failedata?.length -1}`}</Divider>
            }
            // If you want the internal scroll container, give the parent a fixed height & id and pass it here
            // scrollableTarget="scrollableDiv"
          >
            <List
              dataSource={failedata}
              renderItem={(item: any) => (
                <List.Item
                  key={item?.cid ?? item?.uuid ?? Math.random()}
                  className="rounded-lg bg-white/60 px-3"
                >
                  <List.Item.Meta
                    title={
                      <span className="text-sm text-gray-700">
                        {item?.cid !== "NONE" ? item?.cid : item?.uuid ?? "Row"}
                      </span>
                    }
                    description={
                      item?.message ? (
                        <span className="text-xs text-red-500">
                          {item.message}
                        </span>
                      ) : null
                    }
                  />
                </List.Item>
              )}
            />
          </InfiniteScroll>
        )}
      </section>
    </Drawer>
  );
};

export default DisableBeaconsDrawer;
